import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloudUploadOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { Alert, Button, Empty } from 'antd';
import { isEmpty, isEqual } from 'lodash';
import { readableBytes } from '@utils/common';
import { isValidFileFormatForPreview } from '@utils/file';
import { interpolateString } from '@utils/string';
import { uploadFilesApi } from '@api/upload';
import useEffectUpdateOnly from '@hooks/useEffectUpdateOnly';
import useInitialize from '@hooks/useInitialize';
import {
  DOCUMENTATION_LINK_LABEL,
  NOT_EDITABLE_FIELDS,
  VIEW_METADATA_BUTTON_LABEL,
} from '@constants/common';
import {
  DECLINE_BUTTON_LABEL,
  DEFAULT_FILE_SORTING_KEY,
  DEFAULT_PAGE_SIZE,
  DELETE_ALL_CONFIRMATION_MESSAGE,
  DELETE_ALL_FILES_BUTTON_LABEL,
  DELETE_BUTTON_LABEL,
  DELETE_FILE_CONFIRMATION_MESSAGE,
  DELETE_SELECTED_FILE_MESSAGE,
  DELETE_SELECTED_FILES_PLURAL_LABEL,
  DELETE_SELECTED_FILES_SINGULAR_LABEL,
  DOWNLOAD_BUTTON_LABEL,
  EMPTY_FILES_PAGE_SECTION,
  EMPTY_FILES_TABLE_MESSAGE,
  FILE_SORTING_DATATABLE_OPTIONS,
  METADATA_FILTERS_LABEL,
  PREVIEW_FILE_BUTTON_LABEL,
  PREVIEW_FILE_DRAWER_TITLE,
  UPLOAD_BUTTON_LABEL,
  UPLOAD_DESCRIPTION_MESSAGE,
  UPLOAD_FILE_BUTTON_LABEL,
  UPLOAD_FILES_DOCS_URI,
} from '@constants/data-page';
import { IGetFilesPayload, SelectedFilters } from '@constants/data-table';
import { StatusCodes } from '@constants/enum/common';
import {
  deleteAllFiles,
  deleteFile,
  deleteMultipleFiles,
  downloadFile,
  getFileContent,
  getWorkspaceFiles,
  resetFileContent,
  resetFilesMessage,
  selectFileDocument,
  selectSortValue,
  updateFileMeta as updateFileMetaAction,
} from '@redux/actions/fileActions';
import {
  getPipelineMeta,
  selectMetaDataFilterValues,
  setAppliedMetaDataFilterValues,
} from '@redux/actions/metadataFiltersActions';
import {
  fileActionStatusSelector,
  fileContentSelector,
  fileFetchStatusSelector,
  fileMessageSelector,
  filesSelector,
  getFileContentStatusSelector,
  selectedFileDocumentSelector,
  sortValueSelector,
  updateFileMetaStatusSelector,
} from '@redux/selectors/fileSelectors';
import {
  appliedMetaFilterValuesSelector,
  selectedMetaFilterValuesSelector,
} from '@redux/selectors/metadataFiltersSelectors';
import { userSelector } from '@redux/selectors/userSelectors';
import {
  IFile,
  IMessage,
  MetadataFiltersValue,
  PermissionAction,
  PermissionAsset,
} from '@redux/types/types';
import Authorized from '@components/common/Authorized/Authorized';
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator';
import DataTable from '@components/dataTable/DataTable';
import DataTableActions from '@components/dataTable/DataTableActions';
import DrawerMetadataFilters from '@components/drawerMetadataFilters/DrawerMetadataFilters';
import EmptyPageDescription from '@components/emptyPageDescription/EmptyPageDescription';
import MetadataModal from '@components/metadataModal/MetadataModal';
import UploadFileModal from '@components/uploadModal/UploadFileModal';
import { FREE_TIER_LIMITS } from '@modules/Upgrade/consts/pricing';
import SampleFileModal from './features/SampleFileModal';
import styles from './filesPage.module.scss';

const FilesLandingSVG = React.lazy(() => import('@assets/empty/files-landing.svg?react'));
const DocumentsPreview = React.lazy(() => import('@components/documentsPreview/DocumentsPreview'));

const PIPELINE_NAME_FOR_METADATA_ON_FILES_PAGE = 'workspace-pipeline';

enum MenuActions {
  ViewMetadata = 'VIEW_METADATA',
  Delete = 'DELETE',
  Download = 'DOWNLOAD',
}

const FilesPage = () => {
  const dispatch = useDispatch();
  const message: IMessage = useSelector(fileMessageSelector);
  const actionStatus: StatusCodes = useSelector(fileActionStatusSelector);
  const fetchStatus: StatusCodes = useSelector(fileFetchStatusSelector);
  const { data, total } = useSelector(filesSelector);
  const selectedFileDocument = useSelector(selectedFileDocumentSelector);
  const fileContent = useSelector(fileContentSelector);
  const getFileContentStatus = useSelector(getFileContentStatusSelector);
  const selectedSortValue: string = useSelector(sortValueSelector);
  const selectedMetaFilterValues = useSelector(selectedMetaFilterValuesSelector);
  const appliedMetaFilterValues = useSelector(appliedMetaFilterValuesSelector);
  const updateFileMetaStatus = useSelector(updateFileMetaStatusSelector);
  const { isLimitedUser } = useSelector(userSelector);
  const fileWritePermissions = { [PermissionAsset.FILES]: PermissionAction.WRITE };

  const [fileToDelete, setFileToDelete] = useState('');
  const [totalSelectedItems, setTotalSelectedItems] = useState(0);
  const [isUploadModalVisible, setIsUploadModalVisibleTo] = useState(false);
  const [isMetaModalVisible, setIsMetaModalVisible] = useState(false);
  const [currentModalMetaData, setCurrentModalMetaData] = useState<{
    [key: string]: unknown;
    file_id?: string;
    file_name?: string;
  }>({});
  const [openMetadataFilters, setOpenMetadataFilters] = useState(false);
  const [cleanedMetaFilterValues, setCleanedMetaFilterValues] = useState(false);
  const [getFilesWithMetadataFiltersPayload, setGetFilesWithMetadataFiltersPayload] =
    useState<IGetFilesPayload>({
      currentPage: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      searchValue: '',
    });
  const filesDataTableParams = useRef<{
    currentPage: number;
    pageSize: number;
    searchValue: string;
    sortValue?: string;
    filterValues?: SelectedFilters;
    after?: Record<string, unknown>;
    metadataFilterValues?: Record<string, MetadataFiltersValue>;
  } | null>(null);

  const loading =
    actionStatus === StatusCodes.IN_PROGRESS || fetchStatus === StatusCodes.IN_PROGRESS;
  const thereAreNoFiles = fetchStatus === StatusCodes.SUCCESS && data.length === 0;

  // Don't request data until the page is initialized,
  // important for not requesting data with old state
  // (and avoiding unnecesary re-fetching data)
  const { isInitialized } = useInitialize({
    actions: [
      () => dispatch(resetFileContent),
      () => dispatch(resetFilesMessage),
      () => dispatch(selectMetaDataFilterValues({})),
      () => dispatch(setAppliedMetaDataFilterValues({})),
    ],
  });

  useEffect(() => {
    dispatch(getPipelineMeta({ pipelineName: PIPELINE_NAME_FOR_METADATA_ON_FILES_PAGE }));

    return () => {
      dispatch(selectSortValue(DEFAULT_FILE_SORTING_KEY));
    };
  }, []);

  useEffect(() => {
    if (openMetadataFilters) dispatch(selectMetaDataFilterValues(appliedMetaFilterValues));
  }, [openMetadataFilters]);

  const thereAreActiveFilters = () =>
    !!Object.values(filesDataTableParams.current?.filterValues || {}).flat().length ||
    !!Object.values(filesDataTableParams.current?.metadataFilterValues || {}).flat().length ||
    !!filesDataTableParams.current?.searchValue;

  const getFiles = (
    currentPage: number,
    pageSize: number,
    searchValue: string,
    sortValue?: string,
    filterValues?: SelectedFilters,
    after?: Record<string, unknown>,
    metadataFilterValues?: Record<string, MetadataFiltersValue>,
  ) => {
    const afterValue = { value: '', fileId: '' };
    if (after) {
      if ((sortValue && sortValue.includes('name')) || selectedSortValue.includes('name'))
        afterValue.value = after.name as string;
      else afterValue.value = after.created_at as string;
      afterValue.fileId = after.file_id as string;
    }
    const params = {
      currentPage,
      pageSize,
      searchValue,
      sortValue,
      filterValues,
      after: after ? afterValue : undefined,
      metadataFilterValues,
    };

    filesDataTableParams.current = {
      ...params,
      after, // TODO: Standarize 'after' param
    };
    return dispatch(getWorkspaceFiles(params));
  };

  const getFileById = (fileId: string) => {
    return data.find((file: IFile) => file.file_id === fileId);
  };

  const getFileMetadataById = (
    fileId: string,
  ): { [key: string]: unknown; file_id?: string; file_name?: string } => {
    const { meta, name } = getFileById(fileId) || {};
    return { ...meta, file_id: fileId, file_name: name };
  };

  const updateFileMeta = (fileId: string, updateMeta: Record<string, unknown>) => {
    dispatch(updateFileMetaAction({ fileId, meta: updateMeta }));
  };

  useEffectUpdateOnly(() => {
    if (!selectedFileDocument || !fileContent) return;
    if (getFileContentStatus === StatusCodes.SUCCESS) {
      dispatch(selectFileDocument({ ...selectedFileDocument, contents: [{ value: fileContent }] }));
    }
  }, [getFileContentStatus]);

  useEffectUpdateOnly(() => {
    if (updateFileMetaStatus === StatusCodes.SUCCESS) {
      const {
        currentPage = 1,
        pageSize = DEFAULT_PAGE_SIZE,
        searchValue = '',
        sortValue,
        filterValues,
        after,
        metadataFilterValues,
      } = filesDataTableParams.current || {};
      getFiles(
        currentPage,
        pageSize,
        searchValue,
        sortValue,
        filterValues,
        after,
        metadataFilterValues,
      );
      setIsMetaModalVisible(false);
    }
  }, [updateFileMetaStatus]);

  // Refetch files after a successful API action
  useEffectUpdateOnly(() => {
    if (actionStatus === StatusCodes.SUCCESS) {
      const {
        currentPage = 1,
        pageSize = DEFAULT_PAGE_SIZE,
        searchValue = '',
        sortValue,
        filterValues,
        after,
        metadataFilterValues,
      } = filesDataTableParams.current || {};
      getFiles(
        currentPage,
        pageSize,
        searchValue,
        sortValue,
        filterValues,
        after,
        metadataFilterValues,
      );
    }
  }, [actionStatus]);

  const showApplyMetadataFiltersButton = () => {
    return !isEqual(selectedMetaFilterValues, appliedMetaFilterValues);
  };

  const showClearAllMetadataFiltersButton = () => {
    return !isEmpty(selectedMetaFilterValues) || !isEmpty(appliedMetaFilterValues);
  };

  const onViewMetadata = (fileId: string) => {
    setIsMetaModalVisible(true);
    setCurrentModalMetaData(getFileMetadataById(fileId));
  };

  const openDocumentPreview = async (fileId: string, fileName: string) => {
    dispatch(selectFileDocument({ contents: [], fileName, fileId }));
    dispatch(getFileContent({ fileId, fileName }));
  };

  const closeDocumentPreview = () => {
    dispatch(selectFileDocument(null));
  };

  const onDeleteFile = async (fileId: string) => {
    dispatch(deleteFile(fileId));
  };

  const onDeleteMultipleFiles = async (fileIds: string[]) => {
    dispatch(deleteMultipleFiles(fileIds));
  };

  const onDeleteAllFiles = async () => {
    await dispatch(deleteAllFiles());
  };

  const onUpload = () => {
    setIsUploadModalVisibleTo(true);
  };

  const applyMetadataFilters = (metadataFiltersExists: boolean) => {
    const { currentPage, pageSize, searchValue, sortValue, filterValues } =
      getFilesWithMetadataFiltersPayload;
    getFiles(
      currentPage,
      pageSize,
      searchValue,
      sortValue,
      filterValues,
      undefined,
      metadataFiltersExists ? selectedMetaFilterValues : undefined,
    );
  };

  const onClearAllFilters = () => {
    dispatch(selectMetaDataFilterValues({}));
    dispatch(setAppliedMetaDataFilterValues({}));
    applyMetadataFilters(false);
    setCleanedMetaFilterValues(true);
  };

  const onClickApplyMetadataFilters = async () => {
    dispatch(setAppliedMetaDataFilterValues(selectedMetaFilterValues));
    applyMetadataFilters(true);
    setCleanedMetaFilterValues(false);
  };

  const onOpenMetadataFilters = (payload: IGetFilesPayload) => {
    setGetFilesWithMetadataFiltersPayload({ ...payload });
    setOpenMetadataFilters(true);
  };

  const handleMoreActionClick = (key: string, fileName: string, fileId: string) => {
    if (key === MenuActions.ViewMetadata) onViewMetadata(fileId);
    if (key === MenuActions.Delete) setFileToDelete(fileId);
    if (key === MenuActions.Download) dispatch(downloadFile({ fileId, fileName }));
  };

  // Renders

  const renderMetaDataModal = () => {
    return (
      <MetadataModal
        data={currentModalMetaData}
        nonEditableFields={NOT_EDITABLE_FIELDS}
        updating={updateFileMetaStatus === StatusCodes.IN_PROGRESS}
        open={isMetaModalVisible}
        onEdit={(updateMeta: Record<string, unknown>) =>
          updateFileMeta(currentModalMetaData?.file_id ?? '', updateMeta)
        }
        onOk={() => setIsMetaModalVisible(false)}
        onCancel={() => setIsMetaModalVisible(false)}
      />
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: 'table-link-neutral',
      render: (name: string, record: IFile) => (
        <Button
          type="link"
          size="small"
          className={styles.nameLink}
          onClick={() => {
            if (isValidFileFormatForPreview(record)) openDocumentPreview(record.file_id, name);
          }}
        >
          {name}
        </Button>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: '10%',
      render: (size: number) => readableBytes(size),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (created_at: string) => new Date(created_at).toLocaleString(),
    },
    {
      key: 'action',
      width: '10%',
      align: 'right' as const,
      render: (_: never, record: IFile) => {
        const items = [
          {
            label: VIEW_METADATA_BUTTON_LABEL,
            key: MenuActions.ViewMetadata,
          },
          {
            label: DOWNLOAD_BUTTON_LABEL,
            key: MenuActions.Download,
          },
          {
            label: DELETE_BUTTON_LABEL,
            key: MenuActions.Delete,
            danger: true,
            icon: <DeleteOutlined />,
            permissions: fileWritePermissions,
          },
        ];
        return (
          <DataTableActions
            menu={{
              items,
              onClick: ({ key }) => handleMoreActionClick(key, record.name, record.file_id),
              'data-testid': 'files_tableRow_moreActions_button',
            }}
            item={record.file_id}
            itemToDelete={fileToDelete}
            onDelete={onDeleteFile}
            deleteConfirmationMessage={DELETE_FILE_CONFIRMATION_MESSAGE}
            cancelButtonLabel={DECLINE_BUTTON_LABEL}
            onCancelDelete={() => setFileToDelete('')}
            primaryButton={{
              label: PREVIEW_FILE_BUTTON_LABEL,
              action: (fileId: string) => openDocumentPreview(fileId, record.name),
              visible: isValidFileFormatForPreview(record),
            }}
          />
        );
      },
    },
  ];

  const renderEmptyFilesState = () => {
    return (
      <EmptyPageDescription
        title={EMPTY_FILES_PAGE_SECTION.TITLE}
        description={EMPTY_FILES_PAGE_SECTION.DESCRIPTION.map(({ paragraph }) => ({
          paragraph: interpolateString(paragraph, {
            documentationLink: (
              <a href={UPLOAD_FILES_DOCS_URI} target="_blank" rel="noreferrer">
                {DOCUMENTATION_LINK_LABEL}
              </a>
            ),
          }),
        }))}
        image={<FilesLandingSVG />}
        action={
          <>
            <SampleFileModal />
            <Authorized requiredPermissions={fileWritePermissions} renderAsDisabled>
              <Button
                type="primary"
                onClick={onUpload}
                icon={<CloudUploadOutlined />}
                test-id="filesPage_uploadFile_button"
              >
                {UPLOAD_FILE_BUTTON_LABEL}
              </Button>
            </Authorized>
          </>
        }
      />
    );
  };

  const renderFilesListTable = () => {
    const emptyFilesTableMessage = interpolateString(EMPTY_FILES_TABLE_MESSAGE, {
      uploadFilesButton: (
        <Authorized requiredPermissions={fileWritePermissions} renderAsDisabled>
          <Button key="uploadFile" type="link" onClick={onUpload} className="no-padding">
            {UPLOAD_BUTTON_LABEL}
          </Button>
        </Authorized>
      ),
    });

    return (
      <DataTable
        data={data}
        total={total}
        loading={loading || !isInitialized}
        testId="filesTable"
        refetch={isInitialized}
        columns={columns}
        getData={getFiles}
        sorting={{
          selectedValue: selectedSortValue,
          options: FILE_SORTING_DATATABLE_OPTIONS,
        }}
        pagination={{ pageSize: DEFAULT_PAGE_SIZE, cursorPagination: true }}
        primaryAction={{
          label: UPLOAD_FILE_BUTTON_LABEL,
          onClick: onUpload,
          testid: 'filesPage_uploadFile_button',
          icon: <CloudUploadOutlined />,
          disabled: isLimitedUser && total >= FREE_TIER_LIMITS.FILES,
          permissions: fileWritePermissions,
        }}
        filtersAction={{
          secondary: true,
          onClick: (payload?: IGetFilesPayload) => {
            if (payload) onOpenMetadataFilters(payload);
          },
          filtersApplied: !isEmpty(appliedMetaFilterValues),
          testid: 'filesPage_metadataFilters_button',
          icon: <FilterOutlined />,
          tooltipMessage: METADATA_FILTERS_LABEL,
        }}
        appliedMetaFilterValues={appliedMetaFilterValues}
        cleanedMetaFilterValues={cleanedMetaFilterValues}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyFilesTableMessage} />
          ),
        }}
        rowKey="file_id"
        selectActions={[
          {
            type: 'default',
            danger: true,
            label: (totalSelectedItems === 1
              ? interpolateString(DELETE_SELECTED_FILES_SINGULAR_LABEL, {
                  total: totalSelectedItems,
                })
              : interpolateString(DELETE_SELECTED_FILES_PLURAL_LABEL, {
                  total: totalSelectedItems,
                })) as string,
            onClick: onDeleteMultipleFiles,
            popconfirm: {
              title: DELETE_SELECTED_FILE_MESSAGE,
              cancelText: DECLINE_BUTTON_LABEL,
            },
            permissions: fileWritePermissions,
          },
          {
            type: 'default',
            danger: true,
            label: DELETE_ALL_FILES_BUTTON_LABEL,
            onClick: onDeleteAllFiles,
            popconfirm: {
              title: DELETE_ALL_CONFIRMATION_MESSAGE,
              cancelText: DECLINE_BUTTON_LABEL,
            },
            permissions: fileWritePermissions,
          },
        ]}
        setTotalSelectedItems={(totalFilesSelected) => setTotalSelectedItems(totalFilesSelected)}
      />
    );
  };

  return (
    <div data-testid="filesPage" className="content-wrapper">
      {message && message.content && <Alert message={message.content} type={message.type} banner />}
      {thereAreNoFiles && !thereAreActiveFilters()
        ? renderEmptyFilesState()
        : renderFilesListTable()}
      {renderMetaDataModal()}
      {isUploadModalVisible && (
        <UploadFileModal
          multiple
          showUploadList={false}
          onUpload={uploadFilesApi}
          onCancel={() => setIsUploadModalVisibleTo(false)}
          afterUpload={() => getFiles(1, 10, '')}
          uploadDescriptionMessage={UPLOAD_DESCRIPTION_MESSAGE}
          showFileSizeLimitMessage
        />
      )}
      <DrawerMetadataFilters
        openMetadataFilters={openMetadataFilters}
        setOpenMetadataFilters={setOpenMetadataFilters}
        showClearAllMetadataFiltersButton={showClearAllMetadataFiltersButton}
        onClearAllFilters={onClearAllFilters}
        showApplyMetadataFiltersButton={showApplyMetadataFiltersButton}
        onClickApplyMetadataFilters={onClickApplyMetadataFilters}
        pipelineName={PIPELINE_NAME_FOR_METADATA_ON_FILES_PAGE}
      />
      {selectedFileDocument && (
        <React.Suspense fallback={<LoadingIndicator />}>
          <DocumentsPreview
            title={PREVIEW_FILE_DRAWER_TITLE}
            contents={selectedFileDocument.contents}
            closeDocumentPreview={closeDocumentPreview}
            source={selectedFileDocument.fileName}
            meta={getFileMetadataById(selectedFileDocument.fileId || '')}
            onUpdateFileMeta={(updateMeta) =>
              updateFileMeta(selectedFileDocument.fileId, updateMeta)
            }
            loading={getFileContentStatus === StatusCodes.IN_PROGRESS}
            isUpdatingFileMeta={updateFileMetaStatus === StatusCodes.IN_PROGRESS}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default FilesPage;
